<template>
  <ViewContainer
    class="px-0 mx-0"
    style="padding-left: 0 !important; margin-left: 0 !important;"
    :title="board.name"
  >
    <template v-slot:prepend>
      <v-btn
        plain
        icon
        color="blue"
        class="ms-10 px-0"
        :to="{ name: 'boards' }"
        :ripple="false"
        @click="
          trackBoardsActions('boards.Navigate', { section: 'all boards' })
        "
      >
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
    </template>
    <template v-slot:header-right v-if="canEdit">
      <v-icon
        size="16"
        color="blue-grey darken-2"
        data-tourid="boards-name-edit"
        @click="goToEditView"
        >mdi-pencil-outline</v-icon
      >
    </template>
    <template v-slot:append>
      <div class="flex-grow-1">
        <v-chip
          v-if="!canEdit"
          small
          color="blue-grey lighten-2"
          class="blue-grey--text text--darken-3 caption view-only-chip"
        >
          <v-icon size="18" color="blue-grey" class="me-2">
            mdi-eye
          </v-icon>
          {{ $t('boards.view_only') }}
        </v-chip>
      </div>
      <div class="d-flex align-center ms-2">
        <v-btn
          v-if="!showSearch"
          rounded
          icon
          small
          class="me-1"
          color="blue-grey"
          data-tourid="boards-global-search-button"
          @click="handleShowGlobalSearch"
        >
          <v-icon size="18" color="blue-grey darken-1">mdi-magnify</v-icon>
        </v-btn>
        <SearchInput
          v-if="!loadingBoard && showSearch"
          activeClickSearch
          data-tourid="boards-global-search-input"
          :value="globalSearchTerm"
          @clear="trackBoardsActions('boards.clearSearchInput')"
          @clickSearchIcon="handleHideGlobalSearch"
          @change="handleGlobalSearch"
        />
        <ShareSettings :board="board" />
      </div>
    </template>
    <RefreshAlert
      :show="showRefreshAlert"
      @refresh="handleRefresh"
      @dismiss="handleDismissRefreshAlert"
    />
    <div class="columns-container mt-n6 px-1 d-flex" ref="columnsContainer">
      <Draggable
        ghost-class="boards-ghost-item"
        handle=".column-drag-control"
        class="d-inline-flex pb-7"
        :list="columns"
        :scroll-sensitivity="250"
        :force-fallback="true"
        @change="handleDrag"
      >
        <BoardColumn
          v-for="(column, index) in columns"
          :columnIndex="index"
          :canEdit="canEdit"
          :class="{ 'me-4': index < columns.length, 'ms-10': index === 0 }"
          :key="`column-${column.id}`"
          :column="column"
          :loading="loadingColumns"
          :refresh="refreshColumns"
          :globalSearchTerm="globalSearchTerm"
          @delete="handleDeleteColumn(column)"
          @details="handleShowColumnDetails(column)"
          @duplicate="duplicateColumn(column)"
          @updateSort="handleUpdateSort($event, column)"
          @edit="handleEditColumn(column, $event)"
        />
      </Draggable>
      <div class="me-10" v-if="canEdit">
        <v-btn
          v-if="!loadingBoard && !loadingColumns"
          rounded
          small
          data-tourid="boards-add-column"
          class="blue-grey--text text--darken-1 font-size-14 me-10 mt-6"
          color="blue-grey lighten-2"
          elevation="0"
          @click="handleOpenCreateModal"
        >
          <v-icon size="16" class="mr-1" color="blue-grey darken-1">
            mdi-plus
          </v-icon>
          {{ $t('boards.add_column') }}
        </v-btn>
      </div>
      <BoardColumnModal
        v-if="openColumnModal"
        :show="openColumnModal"
        :title="
          currentColumn.id ? $t('boards.edit_column') : $t('boards.add_column')
        "
        :value="currentColumn"
        @close="handleCloseColumnModal"
        @save="handleSaveColumn"
      />
      <DeleteConfirmationModal
        :title="$t('boards.delete_column_title')"
        :text="deleteColumnText"
        :show="showDeleteConfirmationDialog"
        :isLoading="deleting"
        @cancel="handleCancelDeleteColumn"
        @delete="deleteColumn"
      />
      <FilterDetailsDialog
        :show="showDetailDialog"
        :column="currentColumn"
        @close="handleCloseDetailDialog"
      />
    </div>
  </ViewContainer>
</template>

<script>
import { mapGetters } from 'vuex'
import _ from 'lodash'

import api from '@/api'
import mixin from '@/mixins/mixin.vue'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import eventHub, { HUB_EVENTS } from '@/lib/event-hub'

import ViewContainer from '@/components/Core/Layout/ViewContainer.vue'
import DeleteConfirmationModal from '@/components/Modals/DeleteConfirmationModal.vue'

import Draggable from 'vuedraggable'
import BoardColumnModal from './Column/ColumnModal.vue'
import FilterDetailsDialog from '@/containers/BoardView/Column/FilterDetailsDialog.vue'
import BoardColumn from './Column/Column.vue'
import ShareSettings from './ShareSettings.vue'
import RefreshAlert from './RefreshAlert.vue'
import SearchInput from '@/components/Core/SearchInput.vue'

export default {
  name: 'BoardView',

  mixins: [mixin, trackerMixin],

  components: {
    Draggable,
    BoardColumnModal,
    BoardColumn,
    ShareSettings,
    ViewContainer,
    DeleteConfirmationModal,
    FilterDetailsDialog,
    RefreshAlert,
    SearchInput,
  },

  data() {
    return {
      showSearch: false,
      showRefreshAlert: false,
      columns: [],
      boardId: this.$route.params.boardId,
      board: {},
      openColumnModal: false,
      loadingBoard: true,
      loadingColumns: true,
      totalColumns: 0,
      currentColumn: this.$constants.EMPTY_COLUMN,
      showDeleteConfirmationDialog: false,
      deleting: false,
      showDetailDialog: false,
      refreshColumns: false,
      globalSearchTerm: '',
    }
  },

  async mounted() {
    if (!this.organization.featuresEnabled__boards) {
      return this.navigateTo({
        name: 'error404',
      })
    }

    eventHub.$on(HUB_EVENTS.BOARD_VIEW.UPDATED, this.handleItemUpdate)

    await this.getBoard()
    await this.getAllColumns()
  },

  beforeDestroy() {
    eventHub.$off(HUB_EVENTS.BOARD_VIEW.UPDATED)
  },

  computed: {
    ...mapGetters(['organizationId', 'organization', 'accountId']),

    canEdit() {
      return this.accountId === this.board?.createdBy
    },

    deleteColumnText() {
      return this.$t('boards.delete_column_subtitle', {
        name: this.currentColumn?.name,
      })
    },
  },

  methods: {
    handleGlobalSearch(terms) {
      this.globalSearchTerm = terms
      this.trackBoardsActions('boards.searchGlobalTerm', {
        searchTerm: terms,
      })
    },

    async handleHideGlobalSearch() {
      this.globalSearchTerm = ''
      this.showSearch = false
      this.trackBoardsActions('boards.hideGlobalSearch')
    },

    handleShowGlobalSearch() {
      this.showSearch = true
      this.trackBoardsActions('boards.showGlobalSearch')
    },

    handleCloseDetailDialog() {
      this.showDetailDialog = false
      this.currentColumn = this.$constants.EMPTY_COLUMN
    },

    handleItemUpdate() {
      this.trackBoardsActions('boards.ItemUpdated', { board: this.board.id })
      this.showRefreshAlert = true
      this.refreshColumns = false
    },

    handleRefresh() {
      this.showRefreshAlert = false
      this.refreshColumns = true
      this.trackBoardsActions('boards.RefreshBoard', { board: this.board.id })
    },

    handleDismissRefreshAlert() {
      this.trackBoardsActions('boards.DismissRefreshAlert', {
        board: this.board.id,
      })
      this.showRefreshAlert = false
    },

    handleShowColumnDetails(column) {
      this.showDetailDialog = true
      this.currentColumn = column
    },

    handleUpdateSort($event, currentColumn) {
      if (this.canEdit) {
        this.handleSaveColumn({ ...currentColumn, ...$event })
        return
      }
      this.columns = this.columns.map(column =>
        column.id === currentColumn.id ? { ...column, ...$event } : column
      )
    },

    handleCloseColumnModal() {
      this.openColumnModal = false
      this.currentColumn = this.$constants.EMPTY_COLUMN
      this.trackBoardsActions('boards.CancelAction', {
        action: 'edit column',
      })
    },

    updatePositions() {
      this.columns = this.columns.map((column, index) => {
        return {
          ...column,
          position: index,
        }
      })
    },

    async deleteColumn() {
      try {
        this.deleting = true

        await api.board.deleteColumn(
          this.organizationId,
          this.boardId,
          this.currentColumn.id
        )

        this.columns = this.columns.filter(
          column => column.id !== this.currentColumn.id
        )

        await this.updateColumns()
        this.trackBoardsActions('boards.ConfirmAction', {
          action: 'delete column',
        })
        this.showSuccess(this.$t('boards.column_deleted_successfully'))
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.deleting = false
        this.showDeleteConfirmationDialog = false
        this.currentColumn = this.$constants.EMPTY_COLUMN
      }
    },

    handleDeleteColumn(column) {
      this.currentColumn = column
      this.showDeleteConfirmationDialog = true
    },

    getColumnName(name) {
      const maxLength = 40
      const copy = ` ${this.$t('boards.copy')}`

      const availableLength = maxLength - copy.length

      const trimmedName = name.substring(0, availableLength)

      return trimmedName + copy
    },

    async duplicateColumn(originalColumn) {
      try {
        const data = {
          name: this.getColumnName(originalColumn.name),
          filter: originalColumn.filter,
          sort: originalColumn.sort,
          position: originalColumn.position + 1,
        }

        const response = await api.board.createColumn({
          organizationId: this.organizationId,
          boardId: this.boardId,
          data,
        })

        const newColumn = {
          ...response.data,
          filter: data.filter,
        }

        this.columns.splice(data.position, 0, newColumn)

        await this.updateColumns()

        this.showSuccess(this.$t('boards.column_duplicated_successfully'))
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async getBoard() {
      try {
        this.loadingBoard = true
        const response = await api.board.getById({
          organizationId: this.organizationId,
          boardId: this.boardId,
        })

        this.board = response.data

        this.totalColumns = response.data.columnsCount
      } catch (err) {
        if (err.response.status === 404) {
          return this.navigateTo({
            name: 'error404',
            params: {
              title: this.$t('boards.error.404_title'),
              text: this.$t('boards.error.404_text'),

              action: {
                text: this.$t('boards.error.action'),
                to: '/boards',
                color: 'primary',
              },
            },
          })
        }
        this.handleRequestError(err)
      } finally {
        this.loadingBoard = false
      }
    },

    async getAllColumns() {
      if (this.totalColumns === 0) return
      const limit = 5
      const totalPages = Math.ceil(this.totalColumns / limit)

      for (let page = 0; page <= totalPages; page++) {
        this.loadingColumns = true

        try {
          const { results } = await this.getBoardColumns({
            skip: page * limit,
            limit,
          })
          if (results.length) {
            this.columns.push(...this.parseColumns(results))
          }
        } catch (err) {
          this.handleRequestError(err)
        }
      }
      this.loadingColumns = false
    },

    parseColumns(columns) {
      return columns.map(column => ({
        ...column,
        filter: _.omitBy(
          column?.filter,
          (value, key) =>
            (_.isArray(value) && _.isEmpty(value)) ||
            _.isNull(value) ||
            key === '_id'
        ),
      }))
    },

    async getBoardColumns(options) {
      const response = await api.board.getColumns({
        organizationId: this.organizationId,
        boardId: this.boardId,
        limit: options.limit,
        skip: options.skip,
        sort: { by: 'position', order: 'asc' },
      })
      return response.data
    },

    async handleDrag() {
      await this.updateColumns()
      this.trackBoardsActions('boards.DragAndDropColumn', {
        section: 'board view',
      })
    },

    async updateColumns() {
      this.updatePositions()
      try {
        await api.board.updateColumnBulk({
          organizationId: this.organizationId,
          boardId: this.boardId,
          columns: this.columns.map(column => ({
            ...column,
            userFilter: column?.userFilter?.id,
          })),
        })
      } catch (err) {
        this.handleRequestError(err)
      }
    },

    async handleSaveColumn(column) {
      if (!this.canEdit) return
      const updating = !!column?.id
      try {
        if (updating) {
          const newColumnData = {
            name: column.name,
            sort: column.sort,
            position: column.position,
            filter: column?.filter,
          }
          const response = await api.board.updateColumn({
            organizationId: this.organizationId,
            boardId: this.boardId,
            columnId: column.id,
            data: { ...newColumnData, userFilter: column?.userFilter?.id },
          })
          this.columns = this.columns.map(column =>
            column.id === response.data.id
              ? {
                  ...response.data,
                  ...newColumnData,
                }
              : column
          )
          this.showSuccess(this.$t('boards.column_udpated_successfully'))
          this.trackBoardsActions('boards.ConfirmAction', {
            action: 'edit column',
            column: newColumnData,
          })
        } else {
          const response = await api.board.createColumn({
            organizationId: this.organizationId,
            boardId: this.boardId,
            data: {
              ...column,
              position: this.totalColumns,
              userFilter: column?.userFilter?.id,
            },
          })
          this.totalColumns = this.totalColumns + 1
          this.columns.push(...this.parseColumns([response.data]))
          this.showSuccess(this.$t('boards.column_created_successfully'))
          this.trackBoardsActions('boards.ConfirmAction', {
            action: 'create column',
            column,
          })
        }
      } catch (err) {
        this.handleRequestError(err)
      } finally {
        this.currentColumn = this.$constants.EMPTY_COLUMN
        if (!updating) {
          setTimeout(() => {
            this.$refs.columnsContainer.scrollLeft += 350
          }, 200)
        }
      }
    },

    handleEditColumn(column, columnFlags) {
      this.currentColumn = column
      this.openColumnModal = true
      if (columnFlags?.isEmpty) {
        this.trackBoardsActions('boards.OpenEditColumnFiltersEmptyState')
      }
    },

    handleOpenCreateModal() {
      this.currentColumn = this.$constants.EMPTY_COLUMN
      this.openColumnModal = true
      this.trackBoardsActions('boards.OpenAddNewColumnFlow')
    },

    goToEditView() {
      this.navigateTo({
        name: 'editBoard',
        params: {
          boardId: this.boardId,
        },
      })
      this.trackBoardsActions('boards.Navigate', {
        section: 'edit board',
      })
    },

    handleCancelDeleteColumn() {
      this.showDeleteConfirmationDialog = false
      this.trackBoardsActions('boards.CancelAction', {
        action: 'delete column',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.columns-container {
  overflow-y: hidden;
  overflow-x: scroll;
  width: 104%;
}
.view-only-chip:hover::before {
  opacity: 0 !important;
}
</style>
<style lang="scss">
.boards-ghost-item {
  height: 100%;
  margin-top: 26px;
  background-color: $color-blue-grey-lighten2;
  border-radius: 4px;
}
.boards-ghost-item * {
  visibility: hidden;
}
</style>
